import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"
import { toast } from "sonner"
import type { User } from "@/types"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/** Resolve display name from API or legacy stored user shapes */
export function getUserDisplayName(
  user:
    | (Partial<User> & { name?: string; firstName?: string; lastName?: string })
    | null
    | undefined
): string {
  if (!user) return "User";
  const fromParts = [user.firstName, user.lastName].filter(Boolean).join(" ").trim();
  return (
    user.username?.trim() ||
    user.name?.trim() ||
    fromParts ||
    user.email?.split("@")[0] ||
    "User"
  );
}

export function getUserInitial(user: Parameters<typeof getUserDisplayName>[0]): string {
  const name = getUserDisplayName(user);
  return name.charAt(0).toUpperCase();
}

export function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
}

export function formatCurrency(amount: number | null | undefined): string {
  // Handle undefined or null amounts by returning a default value
  if (amount === null || amount === undefined || Number.isNaN(Number(amount))) {
    return 'ZMW 0.00';
  }

  // Format as ZMW with thousands separators (commas)
  return `ZMW ${Number(amount).toLocaleString('en-ZM', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;
}

/** Numeric amount with commas, no currency prefix (for table cells that already show ZMW). */
export function formatNumber(amount: number | null | undefined, digits = 2): string {
  if (amount === null || amount === undefined || Number.isNaN(Number(amount))) {
    return (0).toLocaleString('en-ZM', { minimumFractionDigits: digits, maximumFractionDigits: digits });
  }
  return Number(amount).toLocaleString('en-ZM', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

export function formatShortDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
}

export function calculateDuration(startDate: string, endDate: string): number {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const diffTime = Math.abs(end.getTime() - start.getTime());
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // +1 to include both start and end dates
}

// Function to safely get employee name
export function getEmployeeName(employee?: { firstName?: string; lastName?: string; otherName?: string }): string {
  if (!employee) return 'Unknown Employee';
  return `${employee.firstName || ''} ${employee.lastName || ''}`.trim();
}

// Enhanced local storage functions for app-wide data persistence

// Persist data to localStorage with error handling and notifications
export function saveToLocalStorage<T>(key: string, data: T, showToast = false): void {
  try {
    localStorage.setItem(key, JSON.stringify(data));
    if (showToast) {
      toast.success(`${key.charAt(0).toUpperCase() + key.slice(1)} data saved successfully`);
    }
  } catch (error) {
    console.error(`Error saving data to localStorage for key ${key}:`, error);
    if (showToast) {
      toast.error(`Failed to save ${key} data`);
    }
  }
}

// Retrieve data from localStorage with improved error handling
export function getFromLocalStorage<T>(key: string, defaultValue: T): T {
  try {
    const storedData = localStorage.getItem(key);
    return storedData ? JSON.parse(storedData) : defaultValue;
  } catch (error) {
    console.error(`Error retrieving data from localStorage for key ${key}:`, error);
    return defaultValue;
  }
}

// Remove data from localStorage
export function removeFromLocalStorage(key: string, showToast = false): void {
  try {
    localStorage.removeItem(key);
    if (showToast) {
      toast.success(`${key.charAt(0).toUpperCase() + key.slice(1)} data cleared successfully`);
    }
  } catch (error) {
    console.error(`Error removing data from localStorage for key ${key}:`, error);
    if (showToast) {
      toast.error(`Failed to clear ${key} data`);
    }
  }
}

// Sync mock API data with localStorage for data persistence
export function syncMockApiData<T>(key: string, mockData: T[], fromStorage = true): T[] {
  if (fromStorage) {
    // Try to get data from localStorage first
    const storedData = getFromLocalStorage<T[] | null>(key, null);
    if (storedData && storedData.length > 0) {
      return storedData;
    }
    // If no stored data, save the mock data
    saveToLocalStorage(key, mockData);
  } else {
    // Always save the current data
    saveToLocalStorage(key, mockData);
  }
  return mockData;
}

// Update a specific item in a collection in localStorage
export function updateItemInLocalStorage<T extends { id: string }>(
  key: string, 
  id: string, 
  updates: Partial<T>,
  showToast = false
): T[] | null {
  try {
    const items = getFromLocalStorage<T[]>(key, []);
    const updatedItems = items.map(item => 
      item.id === id ? { ...item, ...updates } : item
    );
    saveToLocalStorage(key, updatedItems, showToast);
    return updatedItems;
  } catch (error) {
    console.error(`Error updating item in localStorage for key ${key}:`, error);
    if (showToast) {
      toast.error(`Failed to update ${key} data`);
    }
    return null;
  }
}

// Add a new item to a collection in localStorage
export function addItemToLocalStorage<T>(
  key: string, 
  item: T,
  showToast = false
): T[] | null {
  try {
    const items = getFromLocalStorage<T[]>(key, []);
    const updatedItems = [...items, item];
    saveToLocalStorage(key, updatedItems, showToast);
    return updatedItems;
  } catch (error) {
    console.error(`Error adding item to localStorage for key ${key}:`, error);
    if (showToast) {
      toast.error(`Failed to add new item to ${key}`);
    }
    return null;
  }
}

// Remove an item from a collection in localStorage
export function removeItemFromLocalStorage<T extends { id: string }>(
  key: string, 
  id: string,
  showToast = false
): T[] | null {
  try {
    const items = getFromLocalStorage<T[]>(key, []);
    const updatedItems = items.filter(item => item.id !== id);
    saveToLocalStorage(key, updatedItems, showToast);
    return updatedItems;
  } catch (error) {
    console.error(`Error removing item from localStorage for key ${key}:`, error);
    if (showToast) {
      toast.error(`Failed to remove item from ${key}`);
    }
    return null;
  }
}
